import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { ShowingInfo } from '../../types';

export default function Section4Showing() {
  const { state, saveSection } = useFormContext();
  const [data, setData] = useState<ShowingInfo>({
    intake_id: '',
  });

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (data.intake_id) return;
    if (state.formData.showing_info) {
      setData(state.formData.showing_info);
    } else if (state.intake) {
      setData(prev => ({ ...prev, intake_id: state.intake!.id }));
    }
  }, [state.formData.showing_info, state.intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Save data when it changes
    if (state.intake && data.intake_id) {
      saveSection('showing_info', data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, state.intake]);

  const updateField = (field: keyof ShowingInfo, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Showing Information</h2>
        
        <div className="space-y-6">
          {/* Showing Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Showing Instructions
            </label>
            <textarea
              value={data.showing_instructions || ''}
              onChange={(e) => updateField('showing_instructions', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={4}
              placeholder="Please provide any special instructions for showing your property (e.g., pets to secure, special lockbox procedures, preferred showing times, etc.)"
            />
          </div>

          {/* Alarm Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Does your property have a security alarm?
            </label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="has_alarm"
                  value="true"
                  checked={data.has_alarm === true}
                  onChange={() => updateField('has_alarm', true)}
                  className="mr-2"
                />
                Yes
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="has_alarm"
                  value="false"
                  checked={data.has_alarm === false}
                  onChange={() => updateField('has_alarm', false)}
                  className="mr-2"
                />
                No
              </label>
            </div>

            {data.has_alarm === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alarm Code
                </label>
                <input
                  type="text"
                  value={data.alarm_code || ''}
                  onChange={(e) => updateField('alarm_code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="Enter alarm code for showings"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This information will be kept secure and only shared with qualified agents and buyers.
                </p>
              </div>
            )}
          </div>

          {/* Property Access */}
          <div className="border-t pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How should we access the property for showings?
            </label>
            <div className="space-y-2">
              {[
                { value: 'code', label: 'There is a code (lockbox / keypad) — I\'ll provide the code' },
                { value: 'key', label: 'It\'s a key — I\'ll give you a key and you can make a copy' },
                { value: 'leave_open', label: 'I prefer to leave the door open' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <label key={option.value} className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="access_method"
                    value={option.value}
                    checked={(data as any).access_method === option.value}
                    onChange={() => updateField('access_method' as any, option.value)}
                    className="mt-0.5 mr-3 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>

            {(data as any).access_method === 'code' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Code
                </label>
                <input
                  type="text"
                  value={(data as any).access_code || ''}
                  onChange={(e) => updateField('access_code' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="Enter the lockbox / keypad code"
                />
              </div>
            )}

            {(data as any).access_method === 'key' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Which door/entry is the key for? (e.g., front door, garage, side door)
                </label>
                <input
                  type="text"
                  value={(data as any).access_key_details || ''}
                  onChange={(e) => updateField('access_key_details' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="e.g., Front door, garage side door"
                />
              </div>
            )}

            {(data as any).access_method === 'other' && (
              <div className="mt-3">
                <input
                  type="text"
                  value={(data as any).access_other || ''}
                  onChange={(e) => updateField('access_other' as any, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="Please describe how we should access the property"
                />
              </div>
            )}
            <p className="text-sm text-gray-500 mt-2">
              This information will be kept secure and only shared with qualified agents and buyers.
            </p>
          </div>

          {/* WiFi Information */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WiFi Network Information (helpful for virtual tours)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Network Name
                </label>
                <input
                  type="text"
                  value={data.wifi_network || ''}
                  onChange={(e) => updateField('wifi_network', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="WiFi network name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="text"
                  value={data.wifi_password || ''}
                  onChange={(e) => updateField('wifi_password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  placeholder="WiFi password"
                />
              </div>
            </div>
          </div>

          {/* Included with Sale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What's included with the sale?
            </label>
            <textarea
              value={data.included_with_sale || ''}
              onChange={(e) => updateField('included_with_sale', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={3}
              placeholder="e.g., Refrigerator, washer/dryer, window treatments, light fixtures, etc."
            />
          </div>

          {/* Excluded with Sale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What's excluded from the sale?
            </label>
            <textarea
              value={data.excluded_with_sale || ''}
              onChange={(e) => updateField('excluded_with_sale', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={3}
              placeholder="e.g., Chandelier in dining room, garden statues, personal items, etc."
            />
          </div>

          {/* Other Property Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Other property notes for showings
            </label>
            <textarea
              value={data.other_property_notes || ''}
              onChange={(e) => updateField('other_property_notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              rows={4}
              placeholder="Any other information that would be helpful for showings (e.g., neighborhood features, parking instructions, nearby amenities, etc.)"
            />
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            This information helps us coordinate smooth and secure showings of your property. 
            All security information will be handled with utmost care and only shared as needed.
          </p>
        </div>
      </div>
    </div>
  );
}
